import { z } from 'zod';
import { CodeEvidenceSchema } from './diagram.js';
// ============= Column Types =============
/**
 * Column data types commonly used in databases.
 */
export const ColumnDataType = z.enum([
    'uuid',
    'string',
    'text',
    'int',
    'bigint',
    'float',
    'decimal',
    'boolean',
    'date',
    'datetime',
    'timestamp',
    'json',
    'jsonb',
    'enum',
    'array',
    'binary',
    'unknown',
]);
/**
 * Database column definition.
 */
export const ColumnSchema = z.object({
    name: z.string(),
    type: ColumnDataType,
    rawType: z.string().optional(), // Original type string
    isPrimaryKey: z.boolean().default(false),
    isForeignKey: z.boolean().default(false),
    isUnique: z.boolean().default(false),
    isNullable: z.boolean().default(true),
    isIndexed: z.boolean().default(false),
    defaultValue: z.string().optional(),
    references: z.object({
        table: z.string(),
        column: z.string(),
    }).optional(),
    description: z.string().optional(),
});
// ============= Relationship Types =============
/**
 * Relationship cardinality types.
 */
export const Cardinality = z.enum([
    '1:1', // One-to-One
    '1:N', // One-to-Many
    'N:1', // Many-to-One
    'N:M', // Many-to-Many
]);
/**
 * Relationship between entities.
 */
export const RelationshipSchema = z.object({
    id: z.string(),
    from: z.string(), // Source entity name
    to: z.string(), // Target entity name
    fromColumn: z.string().optional(), // Source column
    toColumn: z.string().optional(), // Target column
    cardinality: Cardinality,
    label: z.string().optional(),
    isRequired: z.boolean().default(false),
    onDelete: z.enum(['CASCADE', 'SET NULL', 'RESTRICT', 'NO ACTION']).optional(),
    onUpdate: z.enum(['CASCADE', 'SET NULL', 'RESTRICT', 'NO ACTION']).optional(),
});
// ============= Entity Types =============
/**
 * Index definition.
 */
export const IndexSchema = z.object({
    name: z.string().optional(),
    columns: z.array(z.string()),
    isUnique: z.boolean().default(false),
});
/**
 * Database entity (table/model).
 */
export const EntitySchema = z.object({
    id: z.string(),
    name: z.string(),
    tableName: z.string().optional(), // Actual table name if different
    description: z.string().optional(),
    columns: z.array(ColumnSchema),
    primaryKey: z.array(z.string()), // Column names that make up PK
    indexes: z.array(IndexSchema).optional(),
    position: z.object({
        x: z.number(),
        y: z.number(),
    }),
    sourceFile: z.string().optional(),
    sourceType: z.enum(['prisma', 'typeorm', 'sequelize', 'sql', 'mongoose', 'unknown']).optional(),
    /** Code evidence showing where this entity is defined (Issue #178) */
    evidence: z.array(CodeEvidenceSchema).optional(),
});
// ============= Schema Diagram Data =============
/**
 * Complete schema diagram data.
 */
export const SchemaDiagramDataSchema = z.object({
    metadata: z.object({
        title: z.string(),
        description: z.string().optional(),
        databaseType: z.enum(['postgresql', 'mysql', 'sqlite', 'mongodb', 'unknown']).optional(),
        sourceFiles: z.array(z.string()).optional(),
        generatedAt: z.date().optional(),
    }),
    entities: z.array(EntitySchema),
    relationships: z.array(RelationshipSchema),
});
// ============= Detection Result Types =============
/**
 * Prisma model detected from schema.prisma.
 */
export const PrismaModelSchema = z.object({
    name: z.string(),
    tableName: z.string().optional(),
    fields: z.array(z.object({
        name: z.string(),
        type: z.string(),
        isId: z.boolean(),
        isUnique: z.boolean(),
        isOptional: z.boolean(),
        isList: z.boolean(),
        hasDefault: z.boolean(),
        defaultValue: z.string().optional(),
        relation: z.object({
            name: z.string().optional(),
            fields: z.array(z.string()).optional(),
            references: z.array(z.string()).optional(),
            onDelete: z.string().optional(),
            onUpdate: z.string().optional(),
        }).optional(),
        attributes: z.array(z.string()).optional(),
    })),
});
/**
 * Schema detection result.
 */
export const SchemaDetectionResultSchema = z.object({
    prismaModels: z.array(PrismaModelSchema).optional(),
    typeormEntities: z.array(z.object({
        name: z.string(),
        tableName: z.string().optional(),
        columns: z.array(z.object({
            name: z.string(),
            type: z.string(),
            isPrimary: z.boolean(),
            isGenerated: z.boolean(),
            isNullable: z.boolean(),
            isUnique: z.boolean(),
        })),
        relations: z.array(z.object({
            propertyName: z.string(),
            relationType: z.string(),
            targetEntity: z.string(),
            joinColumn: z.string().optional(),
        })),
        sourceFile: z.string(),
    })).optional(),
    sqlTables: z.array(z.object({
        name: z.string(),
        columns: z.array(z.object({
            name: z.string(),
            type: z.string(),
            isPrimaryKey: z.boolean(),
            isForeignKey: z.boolean(),
            isNullable: z.boolean(),
            defaultValue: z.string().optional(),
            references: z.object({
                table: z.string(),
                column: z.string(),
            }).optional(),
        })),
        sourceFile: z.string(),
    })).optional(),
});
// ============= Helper Functions =============
/**
 * Normalize a type string to ColumnDataType.
 */
export function normalizeColumnType(typeStr) {
    const lower = typeStr.toLowerCase();
    if (lower.includes('uuid') || lower.includes('guid'))
        return 'uuid';
    if (lower.includes('varchar') || lower.includes('char') || lower === 'string')
        return 'string';
    if (lower.includes('text') || lower.includes('clob'))
        return 'text';
    if (lower.includes('bigint') || lower.includes('int8'))
        return 'bigint';
    if (lower.includes('int') || lower.includes('integer') || lower.includes('serial'))
        return 'int';
    if (lower.includes('float') || lower.includes('real') || lower.includes('double'))
        return 'float';
    if (lower.includes('decimal') || lower.includes('numeric') || lower.includes('money'))
        return 'decimal';
    if (lower.includes('bool'))
        return 'boolean';
    if (lower.includes('timestamp') || lower.includes('datetime'))
        return 'timestamp';
    if (lower.includes('date'))
        return 'date';
    if (lower.includes('jsonb'))
        return 'jsonb';
    if (lower.includes('json'))
        return 'json';
    if (lower.includes('enum'))
        return 'enum';
    if (lower.includes('array') || lower.includes('[]'))
        return 'array';
    if (lower.includes('binary') || lower.includes('blob') || lower.includes('bytea'))
        return 'binary';
    return 'unknown';
}
/**
 * Map cardinality to Mermaid ER notation.
 */
export function cardinalityToMermaid(cardinality) {
    switch (cardinality) {
        case '1:1':
            return '||--||';
        case '1:N':
            return '||--o{';
        case 'N:1':
            return '}o--||';
        case 'N:M':
            return '}o--o{';
        default:
            return '||--||';
    }
}
/**
 * Get column type badge text.
 */
export function getColumnTypeBadge(column) {
    const badges = [];
    if (column.isPrimaryKey)
        badges.push('PK');
    if (column.isForeignKey)
        badges.push('FK');
    if (column.isUnique && !column.isPrimaryKey)
        badges.push('UQ');
    if (column.isIndexed && !column.isPrimaryKey)
        badges.push('IX');
    return badges.join(' ');
}
/**
 * Convert SchemaDiagramData to UnifiedDiagramData.
 */
export function schemaToUnifiedDiagram(data) {
    const nodes = data.entities.map((entity) => ({
        id: entity.id,
        type: 'database',
        label: entity.name,
        description: entity.description,
        position: entity.position,
        data: {
            tableName: entity.tableName,
            columns: entity.columns,
            primaryKey: entity.primaryKey,
            indexes: entity.indexes,
            sourceType: entity.sourceType,
        },
    }));
    const edges = data.relationships.map((rel) => ({
        id: rel.id,
        source: rel.from,
        target: rel.to,
        label: rel.label || formatCardinality(rel.cardinality),
        type: 'database',
        animated: false,
        data: {
            cardinality: rel.cardinality,
            fromColumn: rel.fromColumn,
            toColumn: rel.toColumn,
            isRequired: rel.isRequired,
            onDelete: rel.onDelete,
        },
    }));
    return {
        metadata: {
            title: data.metadata.title,
            description: data.metadata.description,
            diagramType: 'databaseSchema',
            category: 'data',
            c4Level: 'code',
            layoutDirection: 'LR',
            layoutAlgorithm: 'dagre',
            sourceFiles: data.metadata.sourceFiles,
            generatedAt: data.metadata.generatedAt,
            generatedBy: 'regex',
        },
        nodes,
        edges,
    };
}
/**
 * Format cardinality for display.
 */
function formatCardinality(cardinality) {
    switch (cardinality) {
        case '1:1':
            return 'has one';
        case '1:N':
            return 'has many';
        case 'N:1':
            return 'belongs to';
        case 'N:M':
            return 'has and belongs to many';
        default:
            return '';
    }
}
/**
 * Get entity color based on its role.
 */
export function getEntityColor(entity) {
    // Junction/pivot tables for M:N relationships
    if (entity.name.includes('_') || entity.name.toLowerCase().includes('pivot')) {
        return '#fef3c7'; // Yellow for junction tables
    }
    // User-related tables
    if (entity.name.toLowerCase().includes('user') || entity.name.toLowerCase().includes('account')) {
        return '#dbeafe'; // Blue for user tables
    }
    // Audit/log tables
    if (entity.name.toLowerCase().includes('log') || entity.name.toLowerCase().includes('audit')) {
        return '#f3e8ff'; // Purple for audit tables
    }
    // Default
    return '#e0e7ff'; // Indigo for regular tables
}
//# sourceMappingURL=schema.js.map